import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import { resolveColorContext } from '@quipex/shared/utils';
import { QpxIcon } from '../../../directives';
import type { FilterCardContext } from './types';

@Component({
    selector: 'button[qpx-filter-card], qpx-filter-card',
    imports: [NgIcon, QpxIcon],
    templateUrl: './filter-card.html',
    styleUrl: './filter-card.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class]': 'cardClasses()',
        '[attr.type]': 'hostButtonType',
        '[style.--qpx-filter-card-outline]': 'outlineColor()',
    },
})
export class QpxFilterCard {
    private readonly viewport = injectViewportBreakpoints();
    private readonly isCompactViewport = this.viewport.isPhone;

    //#region Inputs
    public readonly title = input.required<string>();
    public readonly icon = input.required<string>();
    public readonly active = input<boolean>(false);
    public readonly context = input<FilterCardContext>('accent');
    //#endregion

    //#region State
    protected readonly hostButtonType =
        inject(ElementRef<HTMLElement>).nativeElement.tagName.toLowerCase() ===
        'button'
            ? 'button'
            : null;

    protected readonly isCompact = this.isCompactViewport;

    protected readonly cardClasses = computed(() => {
        const { background } = this.contextClasses();
        const activeClasses = this.active()
            ? `${background.surface.className} ${background.foreground.className} outline-[var(--qpx-filter-card-outline)] hover:shadow-sm`
            : 'bg-card text-card hover:bg-accent outline-transparent';

        return `qpx-filter-card shadow-xs transition-colors outline outline-2 ${activeClasses}`;
    });

    protected readonly iconClasses = computed(() => {
        const { background } = this.contextClasses();

        const activeClasses = this.active()
            ? 'active'
            : background.surface.className;

        return `icon ${activeClasses}`;
    });

    protected readonly iconActiveColor = computed(
        () => `var(${this.contextClasses().background.surface.variable})`
    );

    protected readonly iconContext = computed<FilterCardContext | undefined>(
        () => (this.context() === 'accent' ? undefined : this.context())
    );

    private readonly contextClasses = computed(() => {
        const base = resolveColorContext(this.context());
        const background = resolveColorContext(
            this.context() === 'accent' ? 'primary' : this.context(),
            'muted'
        );

        return { background, base };
    });
    protected readonly outlineColor = computed(
        () => `var(${this.contextClasses().base.decorative.variable})`
    );
    //#endregion
}
